import { shrpaApi } from "@repos/axios";

// Calendar ID constants (match server-side ContentCalendarData.cs)
export const LIVE_CALENDAR_PREFIX = "live";
const LEGACY_DEFAULT_CALENDAR_ID = "default";

// Storage keys
const SHOW_TEST_CALENDARS_KEY = "shrpa.calendar.showTestCalendars";

// Per-customer storage key
function getStorageKey(customerId: string): string {
  return `shrpa.calendar.${customerId}.calendarId`;
}

// In-memory cache per customer
const calendarCache = new Map<string, string>();

/**
 * Checks if a calendar ID represents a live (non-test) calendar
 */
export function isLiveCalendar(calendarId: string | null | undefined): boolean {
  if (!calendarId) return false;
  const trimmed = calendarId.trim().toLowerCase();
  return trimmed.startsWith(LIVE_CALENDAR_PREFIX) || trimmed === LEGACY_DEFAULT_CALENDAR_ID;
}

/**
 * Checks if a calendar ID represents a test calendar
 */
export function isTestCalendar(calendarId: string | null | undefined): boolean {
  if (!calendarId) return false;
  return !isLiveCalendar(calendarId);
}

/**
 * Checks if the current calendar for a customer is a test calendar
 */
export async function isCurrentCalendarTest(customerId: string): Promise<boolean> {
  const calendarId = await getCalendarId(customerId);
  return isTestCalendar(calendarId);
}

/**
 * Gets the current calendar ID for a customer (async - may fetch from API)
 */
export async function getCalendarId(customerId: string): Promise<string> {
  if (!customerId) {
    throw new Error("getCalendarId: customerId is required");
  }

  // 1. Check in-memory cache
  const cached = calendarCache.get(customerId);
  if (cached) return cached;

  // 2. Check localStorage
  if (typeof window !== "undefined") {
    const stored = localStorage.getItem(getStorageKey(customerId));
    if (stored) {
      calendarCache.set(customerId, stored);
      return stored;
    }
  }

  // 3. Fetch default from API
  // Keeping this inline to avoid circular dependencies
  const { data } = await shrpaApi.get<string>(`/content-calendar/${customerId}/calendars/default`);
  setCalendarId(customerId, data);
  return data;
}

/**
 * Sets the current calendar ID for a customer
 */
export function setCalendarId(customerId: string, calendarId: string): void {
  if (!customerId || !calendarId) {
    throw new Error("setCalendarId: customerId and calendarId are required");
  }

  calendarCache.set(customerId, calendarId);

  if (typeof window !== "undefined") {
    try {
      localStorage.setItem(getStorageKey(customerId), calendarId);
    } catch (error) {
      throw new Error(`Failed to set calendar ID for ${customerId} in localStorage: ${error}`);
    }
  }
}

/**
 * Gets whether to show test calendars (from localStorage)
 */
export function getShowTestCalendars(): boolean {
  if (typeof window === "undefined") return false;
  const stored = localStorage.getItem(SHOW_TEST_CALENDARS_KEY);
  return stored === "true";
}

/**
 * Sets whether to show test calendars (to localStorage)
 */
export function setShowTestCalendars(value: boolean): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(SHOW_TEST_CALENDARS_KEY, value.toString());
}

export default {
  // Constants
  LIVE_CALENDAR_PREFIX,
  // Functions
  isLiveCalendar,
  isTestCalendar,
  isCurrentCalendarTest,
  getCalendarId,
  setCalendarId,
  getShowTestCalendars,
  setShowTestCalendars,
};
